/* A set's media on the coach's Training tab — the clips and photos a client saved.
 *
 * His decision of 2026-09-05: *"of course a coach can see client's uploaded photos and videos."*
 * WHO may see them is decided by the DATABASE (migration `20260905120000`: a SELECT policy on
 * `form_clips` for the client's ACTIVE coach, and the private bucket's object read derived from the
 * row read) and stated once in Swift (`MediaVisibility`). This module decides nothing about
 * visibility — a rule here would be a second implementation of one the database already enforces,
 * and one this page could never be asked to disagree with. It orders and describes what the read
 * returned, and nothing more.
 *
 * ⚠️ THE ORDER IS THE PICK ORDER (now `setClips.orderClips`), the twin of `MediaAttachment.inPickOrder` (Swift): by
 * the pick's instant, then by place in the pick, a filmed clip (no position) AFTER the pick it
 * shares an instant with, ids breaking exact ties so two readings agree. Nothing here derives an
 * order from when an upload finished — his words: *"some may upload faster but they need to be
 * with the order we selected them in the end."*
 */

export type MediaKind = "video" | "photo";

export type MediaItem = {
  id: string;
  /** 1-based, `form_clips.set_number`. */
  setNumber: number;
  kind: MediaKind;
  /** The object key in the private `form-clips` bucket — read through a SIGNED url, never a public one. */
  storagePath: string;
  /** ISO 8601, `form_clips.recorded_at`. */
  recordedAt: string;
  /** Place in the pick (0-based); null for a clip the camera filmed on its own. */
  position: number | null;
  /** The reps the athlete confirmed for the set the clip counted, when the camera counted. */
  countedReps: number | null;
};

/** F5 (W116) — the camera's count beside a set's play button (`· 8 counted`): DATA, not a label. The newest take of that
 *  set that carries a count wins; null when no take of the set was counted. */
export function countedForSet(items: readonly MediaItem[], setNumber: number): number | null {
  const counted = items.filter((m) => m.setNumber === setNumber && m.countedReps !== null)
    .sort((a, b) => (a.recordedAt < b.recordedAt ? 1 : a.recordedAt > b.recordedAt ? -1 : 0));
  return counted[0]?.countedReps ?? null;
}

/* W116 — `orderMedia`, `describeMedia` and `signedUrlSeconds` lived here for the Training tab's two-step "get link" list.
 * That list was a second player and is gone: a set's media plays from the ONE button (`ClipPlayButton`), its order is
 * `setClips.orderClips` (which kept this file's pick-order rule, position and all), and the stage signs on open. */

/** Turn a PostgREST row of the `form_clips(...)` embed into an item; null for a row this build
 *  cannot read (an unknown kind is refused rather than guessed — the closed vocabulary's spelling
 *  is `video` / `photo`, and a third word means the schema moved). */
export function mediaItem(row: {
  id: string;
  set_number: number;
  media_kind: string;
  storage_path: string;
  recorded_at: string;
  position: number | null;
  counted_reps: number | null;
}): MediaItem | null {
  if (row.media_kind !== "video" && row.media_kind !== "photo") return null;
  return {
    id: row.id,
    setNumber: row.set_number,
    kind: row.media_kind,
    storagePath: row.storage_path,
    recordedAt: row.recorded_at,
    position: row.position,
    countedReps: row.counted_reps,
  };
}
